import {
  FC,
  useEffect,
  useState,
  UIEvent,
  Dispatch,
  SetStateAction,
} from 'react'
import { useParams } from 'react-router-dom'
import { UseQueryResult } from '@tanstack/react-query'
import { SentimentChartSection } from 'src/charts/SentimentChartSection'
import { TopSourcesSection } from 'src/charts/TopSourcesSection'
import { SentimentAnalysesSection } from 'src/charts/SentimentAnalysesSection'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { DailyTrendsSection } from 'src/charts/DailyTrendsSection'
import { useGetAllSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetAllSentimentAnalysisThreadsQuery'
import { useGetPositiveSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetPositiveSentimentAnalysisThreadsQuery'
import { useGetNeutralSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetNeutralSentimentAnalysisThreadsQuery'
import { useGetNegativeSentimentAnalysisThreadsQuery } from 'src/reactQuery/useGetNegativeSentimentAnalysisThreadsQuery'
import {
  AnalyticsFetchThreadsResponse,
  AnalyticsFetchThreadsSentimentType,
} from 'src/services/__generated__/api'
import { OnFilterSelectParams } from 'src/charts/SentimentChartSection/interfaces/onFilterSelect.interface'
import { useGetDailyTrendsByKeywordQuery } from 'src/reactQuery/useGetDailyTrendsByKeywordQuery'
import { useGetSentimentAnalysisByKeywordQuery } from 'src/reactQuery/useGetSentimentAnalysisByKeywordQuery'
import { useGetSentimentChartByKeywordQuery } from 'src/reactQuery/useGetSentimentChartByKeywordQuery'
import { useGetTopSourcesByKeywordQuery } from 'src/reactQuery/useGetTopSourcesByKeywordQuery'
import { getEndOfToday } from 'src/utils/date'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { BackNavigationButton } from 'src/components/BackNavigationButton'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import styles from './keywordDetail.module.scss'
import { ThreadState } from '../KeywordsPage/interfaces/threadState.interface'

const DEFAULT_THREAD_LIMIT = 20
const THREAD_LIMIT_INCREMENT = 10

const INIT_THREAD_STATE: ThreadState = {
  data: null,
  isScrolledDown: false,
  isScrolledUp: false,
  limit: DEFAULT_THREAD_LIMIT,
}

export const KeywordDetail: FC = () => {
  const sessionId = getSessionIdFromLocalStorage()

  const { queryParams } = useQueryParams<GlobalQueryParams>()

  const dates = getDateRangeByQueryParams(queryParams)

  const startDate = dates?.start
  const endDate = dates?.end

  const { themeId, keywordName } = useParams<{
    keywordName: string
    themeId: string
  }>()

  const [filters, setFilters] = useState<OnFilterSelectParams | null>(null)

  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchText])

  const {
    data: dailyTrendsData,
    refetch: refetchDailyTrends,
    isFetching: isDailyTrendsFetching,
  } = useGetDailyTrendsByKeywordQuery({
    campaign: themeId,
    end: endDate || undefined,
    keyword: keywordName,
    start: startDate || undefined,
  })

  const {
    data: sentimentAnalysisData,
    refetch: refetchSentimentAnalysis,
    isFetching: isSentimentAnalysesFetching,
  } = useGetSentimentAnalysisByKeywordQuery({
    campaign: themeId,
    end: endDate || undefined,
    keyword: keywordName,
    start: startDate || undefined,
  })

  const {
    data: sentimentChartData,
    refetch: refetchSentimentChart,
    isFetching: isSentimentChartFetching,
  } = useGetSentimentChartByKeywordQuery({
    campaign: themeId,
    campaigns: filters?.campaigns,
    end: endDate || undefined,
    keyword: keywordName,
    sources: filters?.sources,
    start: startDate || undefined,
  })

  const {
    data: topSourcesData,
    refetch: refetchTopSources,
    isFetching: isTopSourcesFetching,
  } = useGetTopSourcesByKeywordQuery({
    campaign: themeId,
    end: endDate || undefined,
    keyword: keywordName,
    start: startDate || undefined,
  })

  const [allSentimentAnalysisThreads, setAllSentimentAnalysisThreads] =
    useState<ThreadState>({ ...INIT_THREAD_STATE })

  const [
    positiveSentimentAnalysisThreads,
    setPositiveSentimentAnalysisThreads,
  ] = useState<ThreadState>({ ...INIT_THREAD_STATE })

  const [
    negativeSentimentAnalysisThreads,
    setNegativeSentimentAnalysisThreads,
  ] = useState<ThreadState>({ ...INIT_THREAD_STATE })

  const [neutralSentimentAnalysisThreads, setNeutralSentimentAnalysisThreads] =
    useState<ThreadState>({ ...INIT_THREAD_STATE })

  useEffect(() => {
    const resetPagination = (prev: ThreadState) => ({
      ...prev,
      isScrolledDown: false,
      isScrolledUp: false,
      limit: DEFAULT_THREAD_LIMIT,
    })
    setAllSentimentAnalysisThreads(resetPagination)
    setPositiveSentimentAnalysisThreads(resetPagination)
    setNegativeSentimentAnalysisThreads(resetPagination)
    setNeutralSentimentAnalysisThreads(resetPagination)
  }, [debouncedSearchText])

  const getThreadsStateFromSentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ): {
    setState: Dispatch<SetStateAction<ThreadState>>
    state: ThreadState
  } => {
    const sentimentsToThreadsStateMap = {
      ALL: {
        setState: setAllSentimentAnalysisThreads,
        state: allSentimentAnalysisThreads,
      },
      NEG: {
        setState: setNegativeSentimentAnalysisThreads,
        state: negativeSentimentAnalysisThreads,
      },
      NEU: {
        setState: setNeutralSentimentAnalysisThreads,
        state: neutralSentimentAnalysisThreads,
      },
      POS: {
        setState: setPositiveSentimentAnalysisThreads,
        state: positiveSentimentAnalysisThreads,
      },
    }

    return sentiment
      ? sentimentsToThreadsStateMap[sentiment]
      : sentimentsToThreadsStateMap.ALL
  }

  const allSentimentAnalysisThreadsQuery =
    useGetAllSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate,
      keyword: keywordName,
      lastDate: getEndOfToday(),
      limit: allSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      toDate: endDate,
    })

  const positiveSentimentAnalysisThreadsQuery =
    useGetPositiveSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate,
      keyword: keywordName,
      lastDate: getEndOfToday(),
      limit: positiveSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      toDate: endDate,
    })

  const negativeSentimentAnalysisThreadsQuery =
    useGetNegativeSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate,
      keyword: keywordName,
      lastDate: getEndOfToday(),
      limit: negativeSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      toDate: endDate,
    })

  const neutralSentimentAnalysisThreadsQuery =
    useGetNeutralSentimentAnalysisThreadsQuery({
      campaign: themeId,
      fromDate: startDate,
      keyword: keywordName,
      lastDate: getEndOfToday(),
      limit: neutralSentimentAnalysisThreads.limit,
      search: debouncedSearchText || undefined,
      toDate: endDate,
    })

  const getThreadsQueryFromSentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ): UseQueryResult<AnalyticsFetchThreadsResponse | null, unknown> => {
    const sentimentsToThreadsQueryMap = {
      ALL: allSentimentAnalysisThreadsQuery,
      NEG: negativeSentimentAnalysisThreadsQuery,
      NEU: neutralSentimentAnalysisThreadsQuery,
      POS: positiveSentimentAnalysisThreadsQuery,
    }

    return sentiment
      ? sentimentsToThreadsQueryMap[sentiment]
      : sentimentsToThreadsQueryMap.ALL
  }

  const setThreadsDataBySentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ) => {
    const { setState } = getThreadsStateFromSentiment(sentiment)
    const { data } = getThreadsQueryFromSentiment(sentiment)

    if (data) {
      setState((prev) => ({
        ...prev,
        data: data.data || null,
        isScrolledDown: false,
      }))
    }
  }

  useEffect(() => {
    setThreadsDataBySentiment()
  }, [allSentimentAnalysisThreadsQuery.data])
  useEffect(() => {
    setThreadsDataBySentiment(AnalyticsFetchThreadsSentimentType.POS)
  }, [positiveSentimentAnalysisThreadsQuery.data])
  useEffect(() => {
    setThreadsDataBySentiment(AnalyticsFetchThreadsSentimentType.NEG)
  }, [negativeSentimentAnalysisThreadsQuery.data])
  useEffect(() => {
    setThreadsDataBySentiment(AnalyticsFetchThreadsSentimentType.NEU)
  }, [neutralSentimentAnalysisThreadsQuery.data])

  const refetchThreadsBySentiment = (
    sentiment?: AnalyticsFetchThreadsSentimentType
  ) => {
    const { isScrolledUp, isScrolledDown } =
      getThreadsStateFromSentiment(sentiment).state
    const { refetch, isLoading } = getThreadsQueryFromSentiment(sentiment)

    const shouldRefetch = isScrolledUp || isScrolledDown

    if (shouldRefetch && !isLoading) {
      refetch()
    }
  }

  useEffect(() => {
    refetchThreadsBySentiment()
  }, [
    allSentimentAnalysisThreads.isScrolledUp,
    allSentimentAnalysisThreads.isScrolledDown,
  ])
  useEffect(() => {
    refetchThreadsBySentiment(AnalyticsFetchThreadsSentimentType.POS)
  }, [
    positiveSentimentAnalysisThreads.isScrolledUp,
    positiveSentimentAnalysisThreads.isScrolledDown,
  ])
  useEffect(() => {
    refetchThreadsBySentiment(AnalyticsFetchThreadsSentimentType.NEG)
  }, [
    negativeSentimentAnalysisThreads.isScrolledUp,
    negativeSentimentAnalysisThreads.isScrolledDown,
  ])
  useEffect(() => {
    refetchThreadsBySentiment(AnalyticsFetchThreadsSentimentType.NEU)
  }, [
    neutralSentimentAnalysisThreads.isScrolledUp,
    neutralSentimentAnalysisThreads.isScrolledDown,
  ])

  const handleThreadsScroll = (
    event: UIEvent,
    sentiment?: AnalyticsFetchThreadsSentimentType
  ) => {
    if (!event.currentTarget) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget

    const {
      state: { isScrolledUp },
      setState,
    } = getThreadsStateFromSentiment(sentiment)

    const isScrolledCloseToTop = scrollTop < 40
    const isScrolledCloseToBottom = scrollTop + clientHeight > scrollHeight - 40

    if (isScrolledCloseToTop !== isScrolledUp) {
      setState((prev) => ({ ...prev, isScrolledUp: isScrolledCloseToTop }))
    }

    if (isScrolledCloseToBottom) {
      setState((prev) => {
        if (prev.isScrolledDown) return prev
        return {
          ...prev,
          isScrolledDown: true,
          limit: prev.limit + THREAD_LIMIT_INCREMENT,
        }
      })
    }
  }

  useEffect(() => {
    if (sessionId) {
      refetchDailyTrends()

      refetchSentimentAnalysis()

      refetchSentimentChart()

      allSentimentAnalysisThreadsQuery.refetch()
      positiveSentimentAnalysisThreadsQuery.refetch()
      negativeSentimentAnalysisThreadsQuery.refetch()
      neutralSentimentAnalysisThreadsQuery.refetch()

      if (startDate && endDate) {
        refetchTopSources()
      }
    }
  }, [sessionId, themeId, keywordName, startDate, endDate])

  useEffect(() => {
    if (startDate && endDate) {
      refetchSentimentChart()
    }
  }, [filters, startDate, endDate])

  const url = useCustomLink('/keywords')

  return (
    <div className={styles.container}>
      <BackNavigationButton title={keywordName} url={url} />

      <DailyTrendsSection
        chartData={dailyTrendsData}
        isLoading={isDailyTrendsFetching}
      />

      <SentimentChartSection
        chartData={sentimentChartData}
        isLoading={isSentimentChartFetching}
        onFilterSelect={(filters) => setFilters(filters)}
      />

      <SentimentAnalysesSection
        allThreadsData={allSentimentAnalysisThreads.data || undefined}
        chartData={sentimentAnalysisData}
        handleThreadsScroll={handleThreadsScroll}
        isLoading={
          isSentimentAnalysesFetching ||
          allSentimentAnalysisThreadsQuery.isFetching ||
          negativeSentimentAnalysisThreadsQuery.isFetching ||
          neutralSentimentAnalysisThreadsQuery.isFetching ||
          positiveSentimentAnalysisThreadsQuery.isFetching
        }
        negativeThreadsData={negativeSentimentAnalysisThreads.data || undefined}
        neutralThreadsData={neutralSentimentAnalysisThreads.data || undefined}
        onSearchTextChange={setSearchText}
        positiveThreadsData={positiveSentimentAnalysisThreads.data || undefined}
      />

      <TopSourcesSection
        chartData={topSourcesData}
        isLoading={isTopSourcesFetching}
      />
    </div>
  )
}
